import { noop } from 'lodash';
import type { Context, FC, MutableRefObject, PropsWithChildren } from 'react';
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { TooltipDelay } from '../Tooltip';

import type { CoordinatedTooltipProviderOptions } from './CoordinatedTooltip.types';

export interface CoordinatedTooltipOptions {
  readonly delay: TooltipDelay;
  readonly onShow: () => void;
  readonly onHide: () => void;
}

const defaultCoordinatedTooltipContext: CoordinatedTooltipOptions = {
  delay: TooltipDelay.medium,
  onShow: noop,
  onHide: noop,
};

export const CoordinatedTooltipContext: Context<CoordinatedTooltipOptions> = createContext(
  defaultCoordinatedTooltipContext
);
const transactionExpiryTime = 1000;

export const clearTimeoutRef = (ref: MutableRefObject<ReturnType<typeof setTimeout> | undefined>): void => {
  if (ref.current) {
    clearTimeout(ref.current);
  }
};

export const CoordinatedTooltipProvider: FC<PropsWithChildren<CoordinatedTooltipProviderOptions>> = ({
  children,
  delay: initialDelay = defaultCoordinatedTooltipContext.delay,
}) => {
  const endTransactionTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>();
  const setActiveTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>();
  const [delay, setDelay] = useState(initialDelay);

  useEffect(
    () => () => {
      clearTimeoutRef(endTransactionTimeoutRef);
      clearTimeoutRef(setActiveTimeoutRef);
    },
    []
  );

  const onShow = useCallback(() => {
    clearTimeoutRef(endTransactionTimeoutRef);
    setDelay(TooltipDelay.zero);

    // the onShow/onHide may trigger in different orders when quickly moving from one tooltip to another
    setActiveTimeoutRef.current = setTimeout(() => {
      setDelay(TooltipDelay.zero);
    }, 20);
  }, []);

  const reset = useCallback(() => {
    setDelay(initialDelay);
  }, [initialDelay]);

  const onHide = useCallback(() => {
    clearTimeoutRef(endTransactionTimeoutRef);
    endTransactionTimeoutRef.current = setTimeout(reset, transactionExpiryTime);
  }, [reset]);

  const value = useMemo(() => ({ delay, onShow, onHide }), [delay, onShow, onHide]);

  return <CoordinatedTooltipContext.Provider value={value}>{children}</CoordinatedTooltipContext.Provider>;
};
